import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Euro,
  Users,
  CheckCircle,
  ExternalLink,
  Plane,
  Languages,
  FileCheck,
  Home,
  Gift,
  Wallet,
  MessageCircle
} from 'lucide-react';
import { getPageContent } from '../utils/storage';

interface BenefitItem {
  icon: string;
  title: string;
  amount: string;
  description: string;
}

interface StepItem {
  number: string;
  title: string;
  description: string;
}

const iconMap: Record<string, React.ReactNode> = {
  Languages: <Languages className="w-10 h-10" />,
  Plane: <Plane className="w-10 h-10" />,
  Home: <Home className="w-10 h-10" />,
  FileCheck: <FileCheck className="w-10 h-10" />,
};

const Eures: React.FC = () => {
  const [content, setContent] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContent = async () => {
      const pageContent = await getPageContent('eures');
      setContent(pageContent);
      setLoading(false);
    };
    loadContent();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = content.seo_title || 'Consigue Dinero para Trabajar en Europa | Ayudas EURES | TrabajoFuera';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && content.seo_description) {
      metaDescription.setAttribute('content', content.seo_description);
    }
  }, [content]);

  const parseJSON = <T,>(key: string, fallback: T): T => {
    try {
      return content[key] ? JSON.parse(content[key]) : fallback;
    } catch {
      return fallback;
    }
  };

  const benefits: BenefitItem[] = parseJSON('benefits_items', [
    { icon: 'Languages', title: 'Cursos de Idiomas', amount: 'Hasta 1.200€', description: 'Financiación para aprender el idioma del país donde vas a trabajar.' },
    { icon: 'Plane', title: 'Gastos de Viaje', amount: 'Hasta 1.100€', description: 'Ayuda para cubrir tu billete hasta tu nuevo destino laboral.' },
    { icon: 'Home', title: 'Alojamiento Inicial', amount: 'Hasta 760€', description: 'Apoyo económico para los primeros días mientras te instalas.' },
    { icon: 'FileCheck', title: 'Homologación de Títulos', amount: 'Hasta 400€', description: 'Financiación para reconocer tus títulos profesionales.' },
  ]);

  const steps: StepItem[] = parseJSON('steps_items', [
    { number: '1', title: 'Contacta con Nosotros', description: 'Cuéntanos tu situación y qué tipo de trabajo buscas.' },
    { number: '2', title: 'Te Buscamos Trabajo', description: 'Encontramos ofertas que encajan con tu perfil.' },
    { number: '3', title: 'Gestionamos tu Ayuda', description: 'Te ayudamos a solicitar las ayudas EURES.' },
    { number: '4', title: 'Recibes el Dinero', description: 'Una vez aprobado, recibes la financiación.' },
  ]);

  const requirements: string[] = parseJSON('eligibility_requirements', [
    'Tener al menos 18 años',
    'Ser ciudadano de la UE, Noruega o Islandia',
    'Querer trabajar en otro país europeo',
  ]);

  const notRequired: string[] = parseJSON('eligibility_not_required', [
    'Títulos universitarios - vale cualquier nivel',
    'Experiencia previa en el extranjero',
    'Saber el idioma del país de destino',
  ]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('${content.hero_image || 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&q=80'}')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-green-900/90 via-green-800/85 to-emerald-900/90"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="flex justify-center mb-6">
              <div className="bg-yellow-400 p-4 rounded-full">
                <Euro className="w-12 h-12 text-green-900" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
              {content.hero_title || 'Consigue Dinero para Trabajar en el Extranjero'}
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-green-100">
              {content.hero_subtitle || 'La Unión Europea te paga para que encuentres trabajo en otro país'}
            </p>
            <p className="text-lg mb-8 text-green-200">
              {content.hero_description || 'Cursos de idiomas, viajes, alojamiento y más - todo financiado por el programa EURES'}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-yellow-400 hover:bg-yellow-300 text-green-900 px-8 py-4 rounded-full font-bold text-lg transition shadow-lg hover:shadow-xl flex items-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                Solicitar Ayuda Ahora
              </Link>
              <a
                href="https://wa.me/34678601100"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 hover:bg-white/20 text-white border-2 border-white px-8 py-4 rounded-full font-bold text-lg transition flex items-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp Directo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Money Highlight */}
      <section className="py-12 bg-yellow-400">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Wallet className="w-8 h-8 text-green-800" />
              <h2 className="text-2xl md:text-3xl font-bold text-green-900">
                {content.money_highlight_title || 'Hasta 3.460€ en Ayudas Disponibles'}
              </h2>
            </div>
            <p className="text-lg text-green-800">
              {content.money_highlight_subtitle || 'Dinero real que puedes recibir para cubrir tus gastos de traslado y formación'}
            </p>
          </div>
        </div>
      </section>

      {/* Financial Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-800">
            {content.benefits_title || '¿Qué Gastos Te Cubren?'}
          </h2>
          <p className="text-center text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
            {content.benefits_subtitle || 'El programa EURES financia todo lo que necesitas para empezar tu nueva vida laboral en Europa'}
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-green-200"
              >
                <div className="text-green-600 mb-4">
                  {iconMap[benefit.icon] || <Languages className="w-10 h-10" />}
                </div>
                <div className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold inline-block mb-3">
                  {benefit.amount}
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Can Apply */}
      <section className="py-16 bg-green-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Gift className="w-8 h-8 text-yellow-400" />
              <h2 className="text-3xl md:text-4xl font-bold text-center">
                {content.eligibility_title || '¿Puedo Solicitar las Ayudas?'}
              </h2>
            </div>
            <p className="text-center text-xl text-green-200 mb-12">
              {content.eligibility_subtitle || 'Si cumples estos requisitos, puedes recibir dinero para trabajar en Europa'}
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-bold mb-4 text-yellow-400">Requisitos Básicos</h3>
                <ul className="space-y-3">
                  {requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-bold mb-4 text-yellow-400">No Necesitas...</h3>
                <ul className="space-y-3">
                  {notRequired.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works with TrabajoFuera */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-800">
            {content.steps_title || 'Cómo Te Ayudamos a Conseguir el Dinero'}
          </h2>
          <p className="text-center text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
            {content.steps_subtitle || 'En TrabajoFuera gestionamos todo el proceso por ti'}
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="bg-green-600 text-white w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {step.number}
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {content.cta_title || 'No Dejes Pasar Esta Oportunidad'}
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            {content.cta_subtitle || 'Miles de euros disponibles para ayudarte a empezar tu nueva vida en Europa. Nosotros nos encargamos de todo el papeleo.'}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="https://wa.me/34678601100"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-green-700 hover:bg-green-50 px-8 py-4 rounded-full font-bold text-lg transition shadow-lg flex items-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Contactar por WhatsApp
            </a>
            <Link
              to="/contact"
              className="bg-yellow-400 hover:bg-yellow-300 text-green-900 px-8 py-4 rounded-full font-bold text-lg transition shadow-lg flex items-center gap-2"
            >
              <Users className="w-5 h-5" />
              Enviar Consulta
            </Link>
          </div>
        </div>
      </section>

      {/* Official Link */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-gray-600 mb-4">
              {content.footer_text || 'El programa EURES TMS está financiado por la Comisión Europea'}
            </p>
            <a
              href={content.official_link || 'https://eures.europa.eu/eures-services/eures-targeted-mobility-scheme-tms_es'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-green-600 hover:text-green-800 font-semibold"
            >
              Ver información oficial en el portal EURES
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Eures;
